import { useToast } from '@cad-arc';
import { EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { v4 } from 'uuid';
import {
    ActionHandler,
    useIntegratedActionBroadcaster,
} from '../../action-broadcaster/use-integrated-action-broadcaster';
import { useDrawerControls } from '../../drawers/components/drawer-context-provider';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { useWorkspacePanel } from '../../panels/hooks/use-workspace-panel';
import { useSetEventPanelModal } from '../event-panel-modals';
import { RefreshEventPanel } from '../refresh-event-panel';
import { useOpenEventPanel } from '../use-open-event-panel';
import { useEventMetadataStore } from './event-metadata';
import { useSetEventPanelBanners } from '../event-panel-banners';
import { observer } from 'mobx-react-lite';

export const EventPanelActionHandler = observer(
    ({
        form,
        eventId,
        panelUuid,
        refreshEventPanel,
        eventPanelOptions,
        formHasHadDataSetAtLeastOnce,
    }: {
        form: UseFormReturn<EventPanelFormType>;
        eventId?: number;
        panelUuid: string;
        refreshEventPanel: RefreshEventPanel;
        eventPanelOptions: EventPanelOptions;
        formHasHadDataSetAtLeastOnce: boolean;
    }) => {
        const drawerControls = useDrawerControls();
        const openEventPanel = useOpenEventPanel();
        const { trigger } = useIntegratedActionBroadcaster();
        const setEventPanelModal = useSetEventPanelModal();
        const setEventPanelBanners = useSetEventPanelBanners();
        const toast = useToast();
        const integratedForm = useIntegratedForm<EventPanelFormType>();
        const { inputHandleManager } = integratedForm;
        const agencyId = useEventMetadataStore().state.metadata?.agencyId;
        const workspacePanel = useWorkspacePanel<'EVENT'>();
        const isExistingEvent = !!eventId;
        const isNewEvent = !isExistingEvent;
        const handleActions: ActionHandler<'EVENT_V2'> = React.useCallback(
            (action) => {
                if (isNewEvent) {
                    switch (action.type) {
                        case 'FOCUS_PRIMARY_DISPOSITION': {
                            if (workspacePanel.isFocused) {
                                const handle = inputHandleManager.getHandle(
                                    'clearEventPrimaryDisposition'
                                );
                                handle?.ref?.current?.focus();
                                return true;
                            }
                            return false;
                        }
                    }
                }

                if (isExistingEvent && formHasHadDataSetAtLeastOnce) {
                    switch (action.type) {
                        case 'SET_EVENT_PANEL_BANNERS': {
                            if (action.payload.agencyEventId === eventId) {
                                setEventPanelBanners(action.payload.banners);
                            }
                            break;
                        }
                        case 'SHOW_UPDATE_DISPOSITION_MODAL': {
                            if (
                                action.payload.eventId === eventId &&
                                eventPanelOptions &&
                                agencyId
                            ) {
                                setEventPanelModal({
                                    type: 'UPDATE_DISPOSITION',
                                    props: {
                                        eventId,
                                        unitId: action.payload.unitId,
                                        isPrimaryUnit: action.payload.isPrimaryUnit,
                                        agencyId,
                                        refreshEventPanel,
                                        eventPanelOptions,
                                    },
                                });
                                return true;
                            }
                            break;
                        }
                        case 'SHOW_CLEAR_EVENT_MODAL': {
                            if (
                                action.payload.eventId === eventId &&
                                eventPanelOptions &&
                                agencyId
                            ) {
                                setEventPanelModal({
                                    type: 'CLEAR_EVENT',
                                    props: {
                                        eventId,
                                        agencyId,
                                        refreshEventPanel,
                                        eventPanelOptions,
                                    },
                                });
                                return true;
                            }
                            break;
                        }
                        case 'SHOW_COPY_CALL_MODAL': {
                            if (action.payload.agencyEventId === eventId) {
                                setEventPanelModal({
                                    type: 'COPY_CALL',
                                    props: {
                                        agencyEventId: eventId,
                                    },
                                });
                                return true;
                            }
                            break;
                        }
                        case 'OPEN_SERVICE_ROTATION_DRAWER': {
                            if (action.payload.agencyEventId === eventId) {
                                if (action.payload.isTowRotation) {
                                    openEventPanel({
                                        eventId,
                                        shouldFocusPanel: true,
                                        workspacePanelConfig: {
                                            panelState: {
                                                initialTab: 'VEHICLES',
                                            },
                                        },
                                    });
                                    const existingNumberOfTows =
                                        form.getValues('towVehicles').length - 1;

                                    trigger([
                                        {
                                            targetWindowType: 'EVENT_V2',
                                            id: v4(),
                                            type: 'ADD_TOW_VEHICLE_TO_EVENT_FORM',
                                            payload: {
                                                eventId,
                                                initialState: {
                                                    towTypeOption: eventPanelOptions.towTypeOptions.find(
                                                        (o) => o.label === 'Department'
                                                    ),
                                                },
                                            },
                                        },
                                    ]);

                                    drawerControls.open({
                                        type: 'service-rotation',
                                        props: {
                                            eventId,
                                            defaultServiceRotationTypeId:
                                                action.payload.serviceRotationTypeId,
                                            towRotationProps: {
                                                eventForm: form,
                                                towVehicleBaseKey: 'towVehicles',
                                                towVehicleIndex: existingNumberOfTows + 1,
                                            },
                                        },
                                    });
                                } else {
                                    drawerControls.open({
                                        type: 'service-rotation',
                                        props: {
                                            eventId,
                                            defaultServiceRotationTypeId:
                                                action.payload.serviceRotationTypeId,
                                        },
                                    });
                                }

                                return true;
                            }
                            break;
                        }
                        case 'SHOW_REQUEST_ASSISTANCE_MODAL': {
                            if (action.payload.eventId === eventId) {
                                setEventPanelModal({
                                    type: 'REQUEST_ASSISTANCE',
                                    props: {
                                        agencyEventId: eventId,
                                        initialAssistanceAgencyTypes:
                                            action.payload.initialAssistanceAgencyTypes,
                                    },
                                });
                                return true;
                            }
                            break;
                        }

                        case 'SET_EVENT_LOCATION': {
                            if (action.payload.eventId === eventId) {
                                const { locationView, shouldFocusLocationField } = action.payload;
                                if (locationView) {
                                    form.setValue('location', locationView);
                                }

                                if (shouldFocusLocationField) {
                                    const handle = inputHandleManager.getHandle('location');
                                    if (handle) {
                                        handle.setEditableMode?.('edit');
                                        trigger([
                                            {
                                                id: v4(),
                                                targetWindowType: 'EVENT_V2',
                                                type: 'SET_LOCATION_QUERY',
                                                payload: {
                                                    query: locationView?.streetAddress ?? '',
                                                    fieldIdentifier: `${panelUuid}:EVENT_LOCATION`,
                                                },
                                            },
                                        ]);
                                    }
                                }

                                return true;
                            }
                            break;
                        }

                        case 'SET_EVENT_LOCATION_FROM_UNIT_LOCATION': {
                            if (action.payload.eventId === eventId) {
                                const { unitLocation, shouldFocusLocationField } = action.payload;
                                let address = '';
                                if (unitLocation) {
                                    address = `${unitLocation.lat}, ${unitLocation.lng}`;
                                    form.setValue('location', {
                                        // @ts-expect-error form type has stubbed ConsolidatedLocationView
                                        type: 'ADDRESS',
                                        classified: false,
                                        areCoordinatesVerified: false,
                                        departmentId: -1,
                                        entityLinks: [],
                                        latitude: unitLocation.lat,
                                        longitude: unitLocation.lng,
                                        multiAgencyClassificationDtos: [],
                                        streetAddress: address,
                                        id: -1,
                                        spatialReferenceId: -1,
                                    });
                                }

                                if (unitLocation || shouldFocusLocationField) {
                                    const handle = inputHandleManager.getHandle('location');
                                    if (handle) {
                                        handle.setEditableMode?.('edit');
                                        trigger([
                                            {
                                                id: v4(),
                                                targetWindowType: 'EVENT_V2',
                                                type: 'SET_LOCATION_QUERY',
                                                payload: {
                                                    query: address,
                                                    fieldIdentifier: `${panelUuid}:EVENT_LOCATION`,
                                                },
                                            },
                                        ]);
                                    }
                                }
                                if (action.payload.unitLocationIsStale) {
                                    toast({
                                        status: 'default',
                                        description:
                                            'Warning: Unit’s location has not been updated in the last 5 minutes and may be inaccurate. Verify with the unit and ensure you have the right location.',
                                    });
                                }
                                return true;
                            }
                            break;
                        }
                        case 'FOCUS_ACTIVE_EVENT_WINDOW_PANEL': {
                            if (workspacePanel.isFocused) {
                                workspacePanel.zindex = -1;
                                workspacePanel.focus();
                            }
                            break;
                        }
                    }
                }
            },
            [
                isNewEvent,
                isExistingEvent,
                formHasHadDataSetAtLeastOnce,
                workspacePanel,
                inputHandleManager,
                eventId,
                setEventPanelBanners,
                eventPanelOptions,
                agencyId,
                setEventPanelModal,
                refreshEventPanel,
                openEventPanel,
                form,
                trigger,
                drawerControls,
                panelUuid,
                toast,
            ]
        );

        useIntegratedActionBroadcaster({
            id: 'INTEGRATED_EVENT_PANEL_FORM',
            windowType: 'EVENT_V2',
            handler: handleActions,
        });

        return null;
    }
);
